import React from "react";
import { Box, Flex, UnstyledButton } from "@mantine/core";

const ACTIONS = [
  {
    key: "elaborateNotePrompt",
    label: "Elaborate",
    badgeBg: "linear-gradient(135deg, #ff7a59, #ff4d6d)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
        <path d="M4 6h16M4 12h10M4 18h16" />
      </svg>
    ),
  },
  {
    key: "rewriteNotePrompt",
    label: "Rewrite",
    badgeBg: "linear-gradient(135deg, #7856ff, #4f46e5)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
        <path d="M3 21v-4l11-11 4 4-11 11H3z" /><path d="M14 6l4 4" />
      </svg>
    ),
  },
  {
    key: "polishNotePrompt",
    label: "Polish",
    badgeBg: "linear-gradient(135deg, #10b981, #059669)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
        <path d="M12 2l2.4 5.4L20 9l-4.2 3.8L17 19l-5-3-5 3 1.2-6.2L4 9l5.6-1.6L12 2z" />
      </svg>
    ),
  },
];

const roundIconSx = (disabled, active) => ({
  width: 32, height: 32, borderRadius: 999,
  background: active ? "linear-gradient(135deg, rgba(120,86,255,.12), color-mix(in srgb, var(--app-primary) 12%, transparent))" : "var(--app-surface)",
  border: active ? "1px solid #7856ff" : "1px solid var(--app-border)",
  color: active ? "#7856ff" : (disabled ? "var(--app-text-muted)" : "var(--app-text)"),
  boxShadow: active ? "0 0 0 3px rgba(120,86,255,.12)" : "none",
  display: "inline-grid", placeItems: "center",
  cursor: disabled ? "not-allowed" : "pointer",
  "&:hover:not(:disabled)": {
    background: "var(--app-surface-2)",
    color: "#7856ff",
    borderColor: "var(--app-text-muted)",
  },
});

const AiActionButtons = ({
  onMicClick,
  onActionClick,
  onSparklesClick,
  onQuickNotesClick,
  quickNotesDisabled,
  micDisabled,
  actionsDisabled,
  aiActionsOpen,
  sparklesActive,
}) => {
  const sparklesHighlight = sparklesActive !== undefined ? sparklesActive : aiActionsOpen;

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={10}
      px={12}
      py={10}
      sx={{
        borderTop: "1px solid var(--app-border)",
        background: "var(--app-surface-2)",
      }}
    >
      {/* Left: Quick Notes chip (always visible) + Elaborate/Rewrite/Polish action buttons (visible when aiActionsOpen) */}
      <Flex gap={8} align="center" sx={{ flex: 1, overflow: "hidden" }}>
        {onQuickNotesClick && (
          <UnstyledButton
            disabled={quickNotesDisabled}
            onClick={() => !quickNotesDisabled && onQuickNotesClick()}
            title={quickNotesDisabled ? "Select Client Name and Facility Name first" : undefined}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              flex: "0 0 auto",
              padding: "7px 12px",
              background: "#EEF0F3",
              border: "1px solid #E1E4E9",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "Inter",
              color: "#475467",
              cursor: quickNotesDisabled ? "not-allowed" : "pointer",
              opacity: quickNotesDisabled ? 0.55 : 1,
              whiteSpace: "nowrap",
              "&:hover": quickNotesDisabled ? {} : { background: "#E4E7EB", borderColor: "#d2d6dc" },
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 3h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1V4a1 1 0 0 1 1-1z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            Quick Notes
          </UnstyledButton>
        )}
        {aiActionsOpen &&
          ACTIONS.map((action) => (
            <UnstyledButton
              key={action.key}
              disabled={actionsDisabled}
              onClick={() => !actionsDisabled && onActionClick(action.key)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 14px 7px 8px",
                background: "var(--app-surface)",
                border: "1px solid var(--app-border)",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                color: "var(--app-text)",
                cursor: actionsDisabled ? "not-allowed" : "pointer",
                opacity: actionsDisabled ? 0.55 : 1,
                boxShadow: "0 1px 2px rgba(16,24,40,.05)",
                whiteSpace: "nowrap",
                "&:hover": actionsDisabled ? {} : {
                  transform: "translateY(-1px)",
                  boxShadow: "0 1px 3px rgba(16,24,40,.08)",
                  borderColor: "var(--app-text-muted)",
                  color: "var(--app-text)",
                },
              }}
            >
              <Box
                sx={{
                  width: 22, height: 22, display: "grid", placeItems: "center",
                  borderRadius: 7, background: action.badgeBg, color: "white",
                  flex: "0 0 22px",
                }}
              >
                {action.icon}
              </Box>
              {action.label}
            </UnstyledButton>
          ))}
      </Flex>

      {/* Right: mic + sparkles */}
      <Flex gap={4} align="center" sx={{ flexShrink: 0 }}>
        {/* Mic button */}
        <UnstyledButton
          disabled={micDisabled}
          onClick={() => !micDisabled && onMicClick()}
          sx={roundIconSx(micDisabled, false)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="3" width="6" height="12" rx="3" />
            <path d="M5 11a7 7 0 0 0 14 0" />
            <line x1="12" y1="18" x2="12" y2="22" />
          </svg>
        </UnstyledButton>

        {/* Sparkles (AI toggle) */}
        <UnstyledButton
          disabled={actionsDisabled}
          onClick={() => !actionsDisabled && onSparklesClick()}
          sx={roundIconSx(actionsDisabled, sparklesHighlight)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.8 4.7L18 9l-4.2 1.3L12 15l-1.8-4.7L6 9l4.2-1.3L12 3z" />
            <path d="M19 14l.9 2.3L22 17l-2.1.7L19 20l-.9-2.3L16 17l2.1-.7L19 14z" />
          </svg>
        </UnstyledButton>
      </Flex>
    </Flex>
  );
};

export default AiActionButtons;
